import { el, setClass } from './dom';
import { icon } from './Icons';
import { detailShading, type DetailShading } from '@/rendering/DetailShading';

/**
 * The surface-detail switch, in the corner beside the lens.
 *
 * It stands next to the lens row for the same reason the lens row stands next to Render: these are
 * the two things that decide what the frame is *made of* before anyone presses the button. The lens
 * chooses how much of the garden is in it; this chooses whether the sedge and the lawn behind it are
 * distinguishable once they are.
 *
 * A switch rather than a slider, and a switch rather than a setting buried in a panel. The grain is
 * either helping you read the planting or it is in the way — there is no useful 40% of it — and the
 * one moment it is in the way is when you are checking a *colour*, which is exactly the moment you
 * cannot afford to go looking through a menu for the off button.
 */

export interface DetailToggleDeps {
  /** The switch to drive. Injected so the widget can be tested without the module singleton. */
  shading?: DetailShading;
  /** Told after every change, for anything that has to redraw off the back of it. */
  onChange?: (on: boolean) => void;
}

/** What the button says it will do, which is the opposite of what is in force. */
export function describeDetail(on: boolean): string {
  return on
    ? 'Surface detail on — leaf, blade and bark grain, and sun through thin foliage. Click for flat shading.'
    : 'Surface detail off — flat shading. Click to bring back leaf, blade and bark grain.';
}

export class DetailToggle {
  /** Mounted into the render bar's corner row, to the left of the lens. */
  readonly root: HTMLButtonElement;

  private readonly shading: DetailShading;
  private readonly unsubscribe: () => void;

  constructor(private readonly deps: DetailToggleDeps = {}) {
    this.shading = deps.shading ?? detailShading;

    this.root = el('button', {
      class: 'lg-detail',
      attrs: { type: 'button', 'aria-pressed': 'false' },
      html: icon('leaf', 16),
      on: { click: () => this.toggle() },
    });

    // The switch is remembered across sessions and can also be moved by something other than this
    // button, so the button reads it rather than tracking what it last set.
    this.unsubscribe = this.shading.onChange(() => this.refresh());
    this.refresh();
  }

  get value(): boolean {
    return this.shading.enabled;
  }

  private toggle(): void {
    this.shading.setEnabled(!this.shading.enabled);
    this.deps.onChange?.(this.shading.enabled);
  }

  private refresh(): void {
    const on = this.shading.enabled;
    setClass(this.root, 'is-on', on);
    this.root.setAttribute('aria-pressed', String(on));
    this.root.setAttribute('aria-label', describeDetail(on));
    this.root.title = describeDetail(on);
  }

  dispose(): void {
    this.unsubscribe();
    this.root.remove();
  }
}
